import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import ProgressBar from '../../components/elements/ProgressBar';
import { apiFormRequest, apiRequest, formatDate, shortDate } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';

/* The company's own engagement journey — the screen behind the "Your journey" tile.

   Three things live here, in the order the admin needs them:
     1. how far along each of the eight components is, with a way straight to the screen that
        moves it. A percentage with no action next to it is a scoreboard, not a tool.
     2. the name they give the journey and the picture it wears, because both are theirs and not
        a Bravos programme step. The picture is what the badge at the top of every one of their
        people's homes wears — before it, that was a flag icon, and until themes were allowed to
        overlap it was the running theme's picture whenever there was one.
     3. the themes they run, listed with their window, several of which may be running at once.
        READ here and WRITTEN on /themes — two editors for one thing is how they drift. The
        homes no longer name a theme at all: with overlapping windows there is no single one to
        name, so this list (and /themes) is where they are read as the several they are.

   ★ The percentage is PARTICIPATION (answers ÷ people who could answer), and "feedback
   unlocked" is a separate marker. They are different questions: the first is what the admin
   steers, the second is whether we may show results without exposing an individual. */

// Each component points at the screen that actually moves it.
const GO_BY_KEY = {
  readiness: '/bravos/survey/readiness',
  personal_pulse: '/pulses',
  cxo_pulse: '/pulses',
  culture_survey: '/pulses',
  employee_assessment: '/assessments',
  cxo_assessment: '/assessments',
  tickets: '/company-tickets',
  company_signal: '/company-signal',
};

const CompanyJourney = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [name, setName] = useState('');
  // The picture, mid-change: the file they picked (with a local preview) or their decision to
  // drop the stored one. Neither is sent until Save, so the name and the picture travel in one
  // request and one "Saved".
  const [pic, setPic] = useState({ file: null, preview: null, drop: false });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', '/bravos/journey')
      .then((res) => { setData(res); setName(res.name || ''); })
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // An object URL lives until it is revoked, and this screen mints a new one every time they
  // pick another file. The cleanup runs on the swap and on leaving the page.
  useEffect(() => () => { if (pic.preview) URL.revokeObjectURL(pic.preview); }, [pic.preview]);

  const pickPic = (file) => setPic({
    file,
    preview: file ? URL.createObjectURL(file) : null,
    drop: false,
  });

  const save = () => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    // Multipart only when a picture — or the removal of one — rides along: apiRequest always
    // sends JSON, the same split the theme editor makes.
    let send;
    if (pic.file || pic.drop) {
      const form = new FormData();
      form.append('name', name);
      if (pic.file) form.append('file', pic.file);
      else form.append('remove_image', '1');
      send = apiFormRequest('/bravos/journey', form);
    } else {
      send = apiRequest('POST', '/bravos/journey', { name });
    }
    send
      .then((res) => {
        setData((d) => (d ? { ...d, image: res.image || null } : d));
        setPic({ file: null, preview: null, drop: false });
        setNotice(t('saved'));
      })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  // What the badge would wear if they saved now: the file they just picked, else the stored
  // picture, else nothing — which is what "drop" leaves behind.
  const picUrl = pic.preview || (pic.drop ? null : (data && data.image)) || null;
  const today = new Date().toISOString().slice(0, 10);
  const components = (data && data.components) || [];
  const themes = (data && data.themes) || [];

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="flag" theme="primary" className="me-2" />
          {t('yourJourney')}
        </Title>

        {data === null ? (
          <span className="auth__spinner" />
        ) : (
          <>
            <div className="bravos-jhero">
              <div className="bravos-jhero__pct">{data.overall_pct}%</div>
              <div className="bravos-jhero__label">{t('journeyOverall')}</div>
            </div>
            <ProgressBar value={data.overall_pct} />

            {/* The name gets the full width and the button sits under it: side by side, a
                journey called "Building our tribe in 2027" was squeezed into a third of the row
                and read as a truncated placeholder. */}
            <p className="bravos-form__label mt-3">{t('journeyNameLabel')}</p>
            <input
              type="text"
              className="bravos-jname"
              value={name}
              maxLength={120}
              placeholder={t('journeyNamePlaceholder')}
              onChange={(e) => setName(e.target.value)}
            />
            {/* The picture the badge wears. Shown here exactly as it lands there — a square
                crop — so nobody has to open a member's home to find out what they uploaded. */}
            <p className="bravos-form__label mt-3">{t('journeyImageLabel')}</p>
            <div className="bravos-jpic">
              <span className="bravos-jpic__img">
                {picUrl
                  ? <img src={picUrl} alt="" />
                  : <Icon name="flag" theme="primary" />}
              </span>
              <div className="bravos-jpic__side">
                {/* Remounted once the picture is saved, so the picker stops showing the
                    name of a file that has already gone up. */}
                <input
                  key={pic.preview || 'empty'}
                  type="file"
                  accept="image/*"
                  onChange={(e) => pickPic(e.target.files[0] || null)}
                />
                {picUrl && (
                  <button type="button" className="bravos-jpic__drop" onClick={() => setPic({
                    file: null, preview: null, drop: true,
                  })}>
                    <Icon name="trash" className="me-1" />{t('journeyImageRemove')}
                  </button>
                )}
                <span className="bravos-q__hint">{t('journeyImageHint')}</span>
              </div>
            </div>
            <button
              type="button"
              className="bravos-copy mt-2"
              onClick={save}
              disabled={busy}
            >
              <Icon name="check" /> {t('save')}
            </button>
            {notice && <p className="bravos-cal__notice">{notice}</p>}
          </>
        )}
      </Card>

      {data && (
        <Card>
          <Title my="1" className="mt-2">
            <Icon name="list-check" theme="primary" className="me-2" />
            {t('journeyComponents')}
          </Title>
          <p className="bravos-q__hint mb-2">
            {t('journeyComponentsNote', {
              roster: data.roster_total,
              threshold: data.threshold,
            })}
          </p>
          {components.map((c) => (
            <div key={c.key} className="bravos-jrow">
              <div className="bravos-jrow__head">
                <span className="bravos-jrow__name">{c.name}</span>
                <span className="bravos-jrow__num">
                  {t('doneOfTotal', { done: c.done, total: c.total })}
                </span>
                <span className="bravos-jrow__pct">{c.pct}%</span>
              </div>
              <ProgressBar value={c.pct} />
              <div className="bravos-jrow__foot">
                {c.unlocked === true && (
                  <span className="bravos-jrow__unlocked">
                    <Icon name="check" className="me-1" />{t('feedbackUnlocked')}
                  </span>
                )}
                {c.unlocked === false && (
                  <span>{t('feedbackLocked', {
                    n: Math.max(0, data.threshold - c.done),
                  })}</span>
                )}
                <button
                  type="button"
                  className="bravos-jrow__go"
                  onClick={() => navigate(GO_BY_KEY[c.key] || '/member-home')}
                >
                  {t('go')} <Icon name="angle-right" />
                </button>
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* Where the company stands on its OWN events — only for a company that bought the
          engagement product; for anyone else there is nothing to stand on. Deliberately its own
          card rather than a ninth component of the journey: the eight above are the Bravos
          methodology and feed the overall percentage, and a company's townhalls are its own
          programme, not ours. */}
      {data && data.events && (
        <Card>
          <Title my="1" className="mt-2">
            <Icon name="calendar-star" theme="primary" className="me-2" />
            {t('ourEvents')}
          </Title>
          <div className="bravos-stats">
            <div className="bravos-stat">
              <span className="bravos-stat__num">{data.events.upcoming}</span>
              <span className="bravos-stat__label">{t('upcoming')}</span>
              <span className="bravos-stat__sub">
                {t('programmesCount', { n: data.events.programmes })}
              </span>
            </div>
            <div className="bravos-stat">
              <span className="bravos-stat__num">{data.events.going}</span>
              <span className="bravos-stat__label">{t('segment_registered')}</span>
              {/* Answers over INVITATIONS, not over the roster: somebody who was never invited
                  has not failed to answer. */}
              <span className="bravos-stat__sub">
                {t('answeredOf', { n: data.events.answered })}
              </span>
            </div>
          </div>
          {data.events.drafts > 0 && (
            <p className="ce-note">{t('draftsWaiting', { n: data.events.drafts })}</p>
          )}
          {data.events.next && (
            <p className="ce-note">
              {t('nextEventIs', { name: data.events.next.name,
                                  date: shortDate(data.events.next.date, i18n.language) })}
            </p>
          )}
          <button
            type="button"
            className="bravos-jrow__go"
            onClick={() => navigate('/programmes')}
          >
            {t('go')} <Icon name="angle-right" />
          </button>
        </Card>
      )}

      {/* OUR PROGRAMMES — between the events and the themes, because that is the order they
          nest in: a year is grouped into programmes, a programme holds events, and the themes
          run across the whole thing. */}
      {data && data.events && (
        <Card>
          <Title my="1" className="mt-2">
            <Icon name="road" theme="primary" className="me-2" />
            {t('ourProgrammes')}
          </Title>
          <p className="bravos-q__hint mb-2">{t('programmesNote')}</p>
          <div className="bravos-stats">
            <div className="bravos-stat">
              <span className="bravos-stat__num">{data.events.programmes}</span>
              <span className="bravos-stat__label">{t('ourProgrammes')}</span>
            </div>
            <div className="bravos-stat">
              <span className="bravos-stat__num">{themes.length}</span>
              <span className="bravos-stat__label">{t('journeyThemes')}</span>
            </div>
          </div>
          {/* ★ Two counts, two destinations. One button could only go to one of them, and it
              was the programmes — so the theme number on this card was a figure with no way
              out of it. */}
          <div className="bravos-form__row">
            <button
              type="button"
              className="bravos-jrow__go"
              onClick={() => navigate('/programmes')}
            >
              {t('ourProgrammes')} <Icon name="angle-right" />
            </button>
            <button
              type="button"
              className="bravos-jrow__go"
              onClick={() => navigate('/themes')}
            >
              {t('journeyThemes')} <Icon name="angle-right" />
            </button>
          </div>
        </Card>
      )}

      {data && (
        <Card>
          <Title my="1" className="mt-2">
            <Icon name="calendar" theme="primary" className="me-2" />
            {t('journeyThemes')}
          </Title>
          <p className="bravos-q__hint mb-2">{t('journeyThemesNote')}</p>

          {themes.map((th) => {
            const live = (!th.start || th.start <= today) && (!th.end || th.end >= today);
            return (
              <div key={th.id} className="bravos-themerow">
                <span
                  className="bravos-themerow__img"
                  style={th.image ? { backgroundImage: `url("${th.image}")` } : undefined}
                />
                <span className="bravos-themerow__body">
                  <span className="bravos-themerow__name">{th.name}</span>
                  <span className="bravos-themerow__when">
                    {[th.start, th.end].filter(Boolean).map(formatDate).join(' – ')
                      || t('themeNoWindow')}
                  </span>
                </span>
                {live && <span className="bravos-themerow__live">{t('themeLive')}</span>}
              </div>
            );
          })}

          {/* No editor here any more. Themes are written on /themes, which is also where
              what they came to is reported — two editors for one thing is how they drift. */}
          <button
            type="button"
            className="bravos-submit bravos-submit--ghost"
            onClick={() => navigate('/themes')}
          >
            {t('editThemes')} <Icon name="angle-right" />
          </button>
        </Card>
      )}

      {/* OUR BLOGS — what the company puts into its people's reading. Only for a company that
          bought the engagement product, like its events and programmes above: the articles and
          the feeds are how it keeps the year it planned in front of people between the events.
          Counts, the last few titles, and one way in — the writing itself happens on the Blogs
          screen, because two editors for one thing is how they drift apart. */}
      {data && data.content && (
        <Card>
          <Title my="1" className="mt-2">
            <Icon name="newspaper" theme="primary" className="me-2" />
            {t('ourBlogs')}
          </Title>
          <p className="bravos-q__hint mb-2">{t('journeyBlogsNote')}</p>
          <div className="bravos-stats">
            <div className="bravos-stat">
              <span className="bravos-stat__num">{data.content.posts}</span>
              <span className="bravos-stat__label">{t('ourArticles')}</span>
            </div>
            <div className="bravos-stat">
              <span className="bravos-stat__num">{data.content.feeds}</span>
              <span className="bravos-stat__label">{t('ourFeeds')}</span>
              <span className="bravos-stat__sub">
                {t('feedsMaxNote', { max: data.content.feeds_max })}
              </span>
            </div>
          </div>
          {(data.content.latest || []).map((a, i) => (
            <p key={i} className="ce-note">
              {a.title}
              {a.date ? ` · ${shortDate(a.date, i18n.language)}` : ''}
            </p>
          ))}
          {data.content.posts === 0 && data.content.feeds === 0 && (
            <p className="ce-note">{t('noBlogsYet')}</p>
          )}
          <button
            type="button"
            className="bravos-jrow__go"
            onClick={() => navigate('/company-feeds')}
          >
            {t('addArticle')} <Icon name="angle-right" />
          </button>
        </Card>
      )}

      {data && data.methodology && data.methodology.stages.length > 0 && (
        <Card>
          <Title my="1" className="mt-2">
            <Icon name="road" theme="primary" className="me-2" />
            {t('bravosProgramme')}
          </Title>
          <div className="bravos-journey">
            {data.methodology.stages.map((s, i) => (
              <React.Fragment key={s.id}>
                {i > 0 && (
                  <span className={`bravos-journey__link ${s.reached ? 'is-reached' : ''}`} />
                )}
                <div
                  className={`bravos-journey__stage ${s.current ? 'is-current' : ''} ${
                    s.reached ? 'is-reached' : ''
                  }`}
                >
                  <span
                    className="bravos-journey__dot"
                    style={s.reached && s.color ? { backgroundColor: s.color } : undefined}
                  >
                    {s.reached && !s.current ? <Icon name="check" /> : s.order}
                  </span>
                  <span className="bravos-journey__name">{s.name}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
          {(() => {
            const current = data.methodology.stages.find((s) => s.current);
            return current && current.description
              ? <p className="bravos-journey__desc">{current.description}</p>
              : null;
          })()}
        </Card>
      )}
    </Base>
  );
};

export default CompanyJourney;
